import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild,
  EventEmitter,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { EditorComponent, EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { Subscription } from 'rxjs';

const CONTENT_STYLES = `
  body { font-family: Arial, sans-serif; font-size: 14px; color: #1e293b; line-height: 1.5; margin: 8px; }
  p, h1, h2, h3, h4, h5, h6, li, blockquote, pre { margin: 0; padding: 0; line-height: 1.5; }
  ul, ol { margin: 0; padding-left: 1.5em; line-height: 1.5; }
  ul ul, ol ol, ul ol, ol ul { padding-left: 1.25em; }
  .token-badge {
    display: inline-block; padding: 0 4px 0 6px; border-radius: 3px;
    background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af;
    font-size: 10px; font-weight: 600; font-family: monospace;
    cursor: default; user-select: none; white-space: nowrap;
    vertical-align: middle; line-height: 1.8;
  }
  .token-remove {
    display: inline-flex; align-items: center; justify-content: center;
    margin-left: 4px; width: 13px; height: 13px; border-radius: 50%;
    background: rgba(30,64,175,0.15); color: #1e40af;
    font-size: 10px; cursor: pointer; vertical-align: middle;
    font-style: normal; font-weight: 700; line-height: 1; text-decoration: none;
  }
  .token-remove:hover { background: rgba(30,64,175,0.35); }
  /* A chip showing the case's real value reads as prose, not as a code token. */
  .token-badge--value {
    background: #f0fdf4; border-color: #bbf7d0; color: #14532d;
    font-family: Arial, sans-serif; font-size: 13px; font-weight: 500;
  }
  .token-badge--value .token-remove { background: rgba(20,83,45,0.15); color: #14532d; }
  .token-badge--value .token-remove:hover { background: rgba(20,83,45,0.35); }
  /* Present in the CRM but blank — the adviser has something to go and fill in. */
  .token-badge--missing {
    background: #fffbeb; border-color: #fcd34d; color: #92400e;
    font-family: Arial, sans-serif; font-size: 13px; font-weight: 500;
  }
  .token-badge--missing .token-remove { background: rgba(146,64,14,0.15); color: #92400e; }
  .token-badge--missing .token-remove:hover { background: rgba(146,64,14,0.35); }
  /* A region's Start/End markers are structure, not letter content. Once the case's
     list values are in (data-token-list), they stay in the DOM for the save pass but
     are not shown. Template authoring has no list values, so its markers still show. */
  .token-badge[data-token-list][data-token^="#each"],
  .token-badge[data-token-list][data-token="/each"],
  tr[data-token-marker-row]:has([data-token-list]) { display: none; }
  /* A list field the backend serves as HTML (a table, a list of reasons): shown as
     it will print, read-only, and saved back as its token. */
  .token-html-block {
    display: block; margin: 0.25em 0; cursor: default; user-select: none;
    outline: 1px dashed transparent; border-radius: 3px;
  }
  .token-html-block:hover { outline-color: #bbf7d0; }
  .token-html-block:empty::before {
    content: attr(data-token-label); display: inline-block; padding: 0 6px; border-radius: 3px;
    background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af;
    font-size: 10px; font-weight: 600; font-family: monospace; line-height: 1.8;
  }
  .bracket-highlight {
    background: #fef3c7; color: #92400e; border-radius: 3px;
    padding: 0 3px; font-weight: 600;
  }
  /* Long text wraps to the editor's width instead of scrolling sideways: long
     words and URLs break, fixed widths and nowrap/pre styles pasted in with a
     template are capped, and a long case value inside a chip wraps with the line. */
  html { overflow-x: hidden; }
  body { overflow-wrap: anywhere; word-break: break-word; }
  p, div, span, h1, h2, h3, h4, h5, h6, li, blockquote, section, article, figure, img, video, iframe, table {
    max-width: 100% !important; box-sizing: border-box;
  }
  img, video { height: auto; }
  pre, [style*="white-space"] { white-space: pre-wrap !important; }
  .token-badge--value, .token-badge--missing {
    display: inline; white-space: normal; overflow-wrap: anywhere;
    -webkit-box-decoration-break: clone; box-decoration-break: clone;
  }
  table { border-collapse: collapse; width: 100%; font-size: 0.875em; margin: 0.75em 0; }
  table td, table th { border: 1px solid #cbd5e1; padding: 7px 11px; min-width: 3em; vertical-align: top; line-height: 1.5; overflow-wrap: anywhere; word-break: break-word; }
  table thead th { background: #1e293b; color: #f8fafc; font-weight: 600; text-align: left; letter-spacing: 0.02em; }
  table tbody th { background: #f1f5f9; font-weight: 600; color: #1e293b; text-align: left; }
  table tbody tr:nth-child(even) td { background: #f8fafc; }
`;

const FULL_TOOLBAR =
  'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | bullist numlist outdent indent | forecolor backcolor | alignleft aligncenter alignright alignjustify | removeformat | table link image | code fullscreen';

const FULL_PLUGINS =
  'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table wordcount';

/**
 * TinyMCE drops any attribute a rule does not name, so every `data-` attribute a
 * chip relies on has to be listed here or it is gone the moment the editor parses
 * the content. `@[...]` is the global rule: `data-token-repeat` and
 * `data-token-marker-row` land on the `tr`s of an expanded region, not on a span.
 */
const EXTENDED_VALID_ELEMENTS =
  '@[data-token-repeat|data-token-marker-row|data-list-copy|data-token-wrap|data-token|data-token-label|data-token-list|contenteditable],' +
  'span[class|data-token|data-token-label|data-token-list|contenteditable|style],' +
  'em[class|title]';

// Handles the × click inside badges — module-level so config objects are stable references.
const badgeRemoveSetup = (editor: any): void => {
  editor.on('click', (e: any) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('token-remove')) {
      const badge = target.closest('[data-token]');
      if (badge) {
        // Through undoManager, so Ctrl+Z brings a chip removed by mistake back.
        editor.undoManager.transact(() => editor.dom.remove(badge));
        editor.fire('change');
      }
    }
  });
};

const BASE_CONFIG = {
  base_url: '/tinymce',
  suffix: '.min',
  license_key: 'gpl',
  promotion: false,
  branding: false,
  height: '100%' as const,
  menubar: false,
  resize: false,
  content_style: CONTENT_STYLES,
  extended_valid_elements: EXTENDED_VALID_ELEMENTS,
  entity_encoding: 'raw' as const,
  setup: badgeRemoveSetup,
};

const FULL_CONFIG = {
  ...BASE_CONFIG,
  toolbar_mode: 'wrap' as const,
  toolbar: FULL_TOOLBAR,
  plugins: FULL_PLUGINS,
};

const SIMPLE_CONFIG = {
  ...BASE_CONFIG,
  toolbar: false as const,
  plugins: '',
};

/**
 * The app's own font, matching `html { font-family }` in the root stylesheet.
 * Articles render inside the portal, so the editor has to be set in the same
 * face as the page they end up on.
 *
 * Suitability letters deliberately stay on Arial: those are printed documents
 * and the editor has to match their PDF, not the app chrome.
 */
const APP_FONT_STACK = `'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif`;

/** The iframe has its own document, so it loads the webfont itself. */
const APP_FONT_CSS = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';

/**
 * Long-form article authoring.
 *
 * These styles are the ones the published article is rendered with, so the
 * editor is a true preview rather than an approximation. Headings and paragraphs
 * carry real margins: the shared `CONTENT_STYLES` zeroes them, which makes a
 * heading and the paragraph after it look like one block, so authors reformat
 * what they think is one paragraph and change the whole document instead.
 */
const ARTICLE_CONTENT_STYLES = `
  /* Keeps the wheel inside the editor. Without this, reaching the end of the
     article chains the scroll out to the page and carries the toolbar off the
     top of the screen mid-sentence. */
  html, body { overscroll-behavior: contain; }
  /* Left-aligned, not centred: this is a text column, not a printed page, and
     an auto margin leaves a wide empty gutter that reads as a broken indent. */
  body {
    font-family: ${APP_FONT_STACK}; font-size: 14px; color: #334155;
    line-height: 1.7; margin: 0; max-width: 900px; padding: 20px 24px 32px;
  }
  h1, h2, h3, h4, h5, h6 { color: #0f172a; font-weight: 600; line-height: 1.3; margin: 1.5em 0 0.5em; }
  h1 { font-size: 24px; } h2 { font-size: 20px; } h3 { font-size: 16px; }
  h4 { font-size: 14px; } h5, h6 { font-size: 13px; }
  h1:first-child, h2:first-child, h3:first-child { margin-top: 0; }
  p { margin: 0 0 1em; }
  ul { list-style: disc; padding-left: 1.5em; margin: 0 0 1em; }
  ol { list-style: decimal; padding-left: 1.5em; margin: 0 0 1em; }
  li { margin: 0.25em 0; }
  a { color: #4f46e5; text-decoration: underline; }
  img { max-width: 100%; height: auto; border-radius: 8px; }
  figure { margin: 0 0 1em; }
  figure figcaption { margin-top: 0.5em; font-size: 12px; color: #64748b; text-align: center; }
  blockquote {
    border-left: 3px solid #c7d2fe; background: #eef2ff;
    padding: 0.75em 1em; margin: 0 0 1em; color: #334155;
  }
  hr { border: 0; border-top: 1px solid #e2e8f0; margin: 2em 0; }
  pre { background: #0f172a; color: #e2e8f0; padding: 12px 14px; border-radius: 8px; overflow: auto; margin: 0 0 1em; }
  code { background: #f1f5f9; border-radius: 4px; padding: 1px 4px; font-size: 0.9em; }
  pre code { background: transparent; padding: 0; }
  table { border-collapse: collapse; width: 100%; font-size: 0.875em; margin: 1em 0; }
  table td, table th { border: 1px solid #cbd5e1; padding: 7px 11px; vertical-align: top; text-align: left; }
  table thead th { background: #1e293b; color: #f8fafc; font-weight: 600; }
  table tbody tr:nth-child(even) td { background: #f8fafc; }
`;

const ARTICLE_TOOLBAR =
  'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | ' +
  'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | blockquote hr | ' +
  'link image media table codesample charmap emoticons | removeformat | visualblocks preview code fullscreen';

const ARTICLE_PLUGINS =
  'advlist autolink lists link image media table charmap emoticons codesample ' +
  'anchor searchreplace visualblocks nonbreaking insertdatetime preview code fullscreen wordcount quickbars';

/**
 * Opens a local file and hands it back as a data URL. There is no image-upload
 * endpoint, so pictures live inside the article HTML — which is also why pasted
 * screenshots work. It does inflate the stored body, so very large images are
 * worth resizing before they go in.
 */
/**
 * Word's Enter behaviour: finishing a heading and pressing Enter drops you into
 * body text. Without this the new block stays a heading, so everything typed
 * after it inherits the format — which is why reformatting one line appeared to
 * restyle the whole article, and why plain text came out bold.
 *
 * Splitting a heading mid-line still leaves two headings, which is correct.
 */
const exitHeadingOnEnter = (editor: any): void => {
  editor.on('keyup', (event: KeyboardEvent) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    const node = editor.selection.getNode() as HTMLElement | null;
    if (!node || !/^h[1-6]$/i.test(node.nodeName)) return;
    // Only the freshly created, still-empty heading.
    if ((node.textContent ?? '').trim()) return;
    editor.execCommand('FormatBlock', false, 'p');
  });
};

/**
 * Brings the toolbar back into view whenever the author starts working, for the
 * case where the page was scrolled past it first. `toolbar_sticky` only tracks
 * the window, so it cannot help when the host page scrolls in its own container.
 */
/** The nearest ancestor that actually scrolls, or null when the page does. */
const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  for (let node = element?.parentElement ?? null; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
};

const keepToolbarInView = (editor: any): void => {
  const reveal = () => {
    const container = editor.getContainer() as HTMLElement | null;
    const header = (container?.querySelector('.tox-editor-header') ?? container) as HTMLElement | null;
    if (!header) return;

    // Measured against the scroll container, not the viewport: a host page with
    // a pinned header of its own covers the top of the window, so a toolbar
    // hidden behind it still reports a positive distance from the viewport top.
    const scroller = findScrollParent(container);
    const limit = scroller ? scroller.getBoundingClientRect().top : 0;
    if (header.getBoundingClientRect().top < limit + 4) {
      // Instant, not smooth — a smooth scroll gets cut short by the browser's
      // own focus handling and stops partway.
      header.scrollIntoView({ block: 'start' });
    }
  };
  // Deliberately not on keydown: re-aligning the page on every keystroke fights
  // the editor's own scroll-to-caret and leaves the line being typed off-screen.
  editor.on('focus', reveal);
  editor.on('click', reveal);

  // The editor's own viewport follows the caret. Enter is the case that needs
  // saying explicitly — a new block is measured after the scroll would have
  // happened, so the fresh line can land just under the bottom edge.
  editor.on('keyup', (event: KeyboardEvent) => {
    if (event.key === 'Enter') editor.selection.scrollIntoView();
  });
};

const articleSetup = (editor: any): void => {
  badgeRemoveSetup(editor);
  exitHeadingOnEnter(editor);
  keepToolbarInView(editor);
};

const pickLocalImage = (callback: (url: string, meta: Record<string, string>) => void): void => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(String(reader.result ?? ''), { title: file.name, alt: file.name }));
    reader.readAsDataURL(file);
  });
  input.click();
};

const ARTICLE_CONFIG = {
  ...BASE_CONFIG,
  content_style: ARTICLE_CONTENT_STYLES,
  toolbar_mode: 'wrap' as const,
  toolbar: ARTICLE_TOOLBAR,
  plugins: ARTICLE_PLUGINS,
  setup: articleSetup,
  // No `toolbar_sticky`: it docks the toolbar off `window.scrollY`, and this
  // page scrolls inside its own container, so the window never moves and
  // TinyMCE parks the toolbar behind the app's topbar. Left in normal flow it
  // is always at the top of the editor, and `keepToolbarInView` handles the
  // case where the page has been scrolled past it.
  content_css: [APP_FONT_CSS],
  // Inter first, with a value string identical to the one on `body`, so the
  // dropdown resolves the current font instead of reporting whatever it matches
  // first in its own default list (which showed Book Antiqua).
  font_family_formats:
    `Inter=${APP_FONT_STACK};` +
    'Arial=arial,helvetica,sans-serif;' +
    'Calibri=calibri,candara,segoe ui,sans-serif;' +
    'Georgia=georgia,palatino,serif;' +
    'Helvetica=helvetica,arial,sans-serif;' +
    'Tahoma=tahoma,arial,helvetica,sans-serif;' +
    'Times New Roman=times new roman,times,serif;' +
    'Trebuchet MS=trebuchet ms,geneva,sans-serif;' +
    'Verdana=verdana,geneva,sans-serif;' +
    'Courier New=courier new,courier,monospace',
  font_size_formats: '12px 14px 16px 18px 20px 24px 30px 36px',
  font_size_input_default_unit: 'px',
  // Every Enter makes a real block, so reformatting one heading cannot reach
  // the paragraph after it.
  forced_root_block: 'p' as const,
  newline_behavior: 'block' as const,
  // Drag handles on images and tables, plus the size/caption controls.
  object_resizing: true,
  resize_img_proportional: true,
  image_advtab: true,
  image_caption: true,
  image_dimensions: true,
  image_title: true,
  file_picker_types: 'image',
  file_picker_callback: (callback: (url: string, meta: Record<string, string>) => void) => pickLocalImage(callback),
  // Screenshots pasted straight from the clipboard become inline data URLs.
  paste_data_images: true,
  automatic_uploads: false,
  media_live_embeds: true,
  table_default_attributes: { class: '' },
  // Floating toolbars: one when text is selected, one when an image is.
  quickbars_selection_toolbar: 'bold italic underline | quicklink h2 h3 blockquote',
  quickbars_insert_toolbar: 'quickimage quicktable hr',
  // No rotate/imageoptions here: those come from imagetools, which is not part
  // of TinyMCE 8 GPL. `image` re-opens the dialog, where image_advtab holds the
  // size fields; dragging the corner handles resizes in place.
  quickbars_image_toolbar: 'alignleft aligncenter alignright | image | link',
};

/**
 * How a chip is showing its token:
 * `label` — the token's display name, because there is no single value for it;
 * `value` — the case's real value; `missing` — present in the CRM but blank.
 */
export type TokenBadgeState = 'label' | 'value' | 'missing';

/** The catalogue's `insertText` plus the text its chip should carry. */
export interface EditorToken {
  /** Exact string the API says to insert, e.g. `{{CLIENT_1_FIRSTNAME}}`. */
  insertText: string;
  label: string;
}

export function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The key a chip stores, taken from the catalogue's `insertText`:
 * `{{CLIENT_1_FIRSTNAME}}` -> `CLIENT_1_FIRSTNAME`, `{{#each SHAREHOLDERS}}` ->
 * `#each SHAREHOLDERS`. `badgesToTokens` puts the braces back, so a marker chip
 * round-trips to exactly the marker the backend handed over.
 */
export function tokenKeyFromInsertText(insertText: string): string {
  const match = /^\s*\{\{([\s\S]+)\}\}\s*$/.exec(insertText ?? '');
  return (match ? match[1] : (insertText ?? '')).trim();
}

/**
 * Wrap a token into a badge span with a removable × button.
 *
 * `text` is whatever the chip should read — a label or a live case value — and is
 * escaped, so a value like `income < £30k` cannot break out of the chip. Nothing
 * inside is a `<span>`: the backend's fallback converter matches up to the first
 * `</span>`, and a nested one would cut the chip in half.
 */
export function tokenBadgeHtml(
  key: string,
  text: string,
  options: { state?: TokenBadgeState; label?: string; listKey?: string } = {},
): string {
  const state = options.state ?? 'label';
  const stateClass = state === 'label' ? '' : ` token-badge--${state}`;
  // The label is kept on the element so "Refresh values" can fall back to it
  // without re-reading the whole token catalogue.
  const label = escapeHtml(options.label ?? text);
  // A chip reading a repeating region — either the region's own marker or a field
  // inside it. Its text comes from the letter's `lists` block, which `/token-values`
  // does not serve, so "Refresh values" has to leave it alone.
  const listAttribute = options.listKey ? ` data-token-list="${escapeHtml(options.listKey)}"` : '';
  return `<span class="token-badge${stateClass}" data-token="${escapeHtml(key)}" data-token-label="${label}"${listAttribute} contenteditable="false">${escapeHtml(text)}<em class="token-remove" title="Remove">×</em></span>​`;
}

/** What a chip should read, from the three states the token-values API returns. */
export function tokenValueDisplay(
  key: string,
  label: string,
  values: Record<string, string | null> | null | undefined,
): { text: string; state: TokenBadgeState } {
  // A missing key means there is no single value — a list marker, a table token
  // or an unknown one. `null` means the CRM holds the field but it is blank, so
  // the two have to be told apart by presence, not by falsiness.
  if (!values || !Object.prototype.hasOwnProperty.call(values, key)) {
    return { text: label, state: 'label' };
  }
  const value = values[key];
  // Shown exactly as the API sends it — dates included (`30-09-2026`), since the
  // backend already formats them the way the letter prints them.
  const text = value === null || value === undefined ? '' : String(value);
  return text ? { text, state: 'value' } : { text: `${label} - not recorded`, state: 'missing' };
}

/**
 * What a region's Start marker should read: the letter repeats it once per item,
 * so the chip carries the count the case actually holds.
 *
 * A marker keeps the plain chip look even with a count on it — it is structure,
 * not prose. Zero items is the exception: the whole region prints nothing, which
 * is worth flagging in the same warning style as a blank value.
 */
export function tokenListSummaryDisplay(
  label: string,
  itemCount: number | null | undefined,
): { text: string; state: TokenBadgeState } {
  if (itemCount === null || itemCount === undefined || !Number.isFinite(itemCount)) {
    return { text: label, state: 'label' };
  }
  const count = Math.max(0, Math.trunc(itemCount));
  if (!count) return { text: `${label} - no items`, state: 'missing' };
  return { text: `${label} - ${count} ${count === 1 ? 'item' : 'items'}`, state: 'label' };
}

/** Rewrites a live chip's text in place, keeping its × button and its key. */
export function setTokenBadgeContent(
  badge: HTMLElement,
  display: { text: string; state: TokenBadgeState },
): void {
  const removeButton = badge.querySelector('.token-remove');
  badge.textContent = display.text;
  if (removeButton) badge.append(removeButton);
  badge.classList.remove('token-badge--value', 'token-badge--missing');
  if (display.state !== 'label') badge.classList.add(`token-badge--${display.state}`);
}

/**
 * A Start marker and its End as one insert. Every Start needs a matching End:
 * the backend strips a stray marker so it never prints, but the region between
 * them then renders wrongly, so the two never go in separately.
 */
export function tokenRegionHtml(start: EditorToken, end: EditorToken, middle = ''): string {
  const startHtml = tokenBadgeHtml(tokenKeyFromInsertText(start.insertText), start.label);
  const endHtml = tokenBadgeHtml(tokenKeyFromInsertText(end.insertText), end.label);
  const body = middle ? ` ${escapeHtml(middle)} ` : ' ';
  return `${startHtml}${body}${endHtml}`;
}

/** `[Adviser Name]`-style placeholders the author still has to fill in. */
const bracketPattern = (): RegExp => /\[[^\][]+\]/g;

/**
 * Tints every `[placeholder]` so unfilled spots stand out while editing. Walks
 * text nodes rather than running a regex over the raw HTML, so bracketed content
 * inside tags and attributes (`<a href="/x[1]">`) is left alone. The spans stay
 * editable — they are presentation only, and `stripBracketHighlights` removes
 * them again before the HTML is saved or rendered.
 */
export function highlightBrackets(html: string): string {
  if (typeof document === 'undefined' || !html) return html;

  const root = document.createElement('div');
  root.innerHTML = html;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    // Token badges carry their own styling; never nest a highlight inside one.
    if (text.parentElement?.closest('[data-token], .bracket-highlight')) continue;
    if (bracketPattern().test(text.data)) targets.push(text);
  }

  for (const text of targets) {
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    for (const match of text.data.matchAll(bracketPattern())) {
      const start = match.index ?? 0;
      if (start > cursor) fragment.append(text.data.slice(cursor, start));
      const span = document.createElement('span');
      span.className = 'bracket-highlight';
      span.textContent = match[0];
      fragment.append(span);
      cursor = start + match[0].length;
    }
    if (cursor < text.data.length) fragment.append(text.data.slice(cursor));
    text.replaceWith(fragment);
  }

  return root.innerHTML;
}

/** Unwraps the highlight spans, restoring plain `[placeholder]` text. */
export function stripBracketHighlights(html: string): string {
  if (typeof document === 'undefined' || !html) return html;

  const root = document.createElement('div');
  root.innerHTML = html;
  root
    .querySelectorAll('.bracket-highlight')
    .forEach((el) => el.replaceWith(el.textContent ?? ''));
  return root.innerHTML;
}

@Component({
  selector: 'app-tinymce-editor',
  standalone: true,
  imports: [CommonModule, EditorModule, ReactiveFormsModule],
  template: `
    @if (isBrowser) {
      <editor [init]="activeEditorConfig" [formControl]="innerControl" (onInit)="editorReady.emit()"></editor>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        width: 100%;
      }
      /* Make the Angular EditorComponent host fill :host */
      :host ::ng-deep editor {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }
      /* TinyMCE root fills the editor component;
         height: 100% set by TinyMCE init is then relative to this resolved height */
      :host ::ng-deep .tox-tinymce {
        flex: 1 !important;
        min-height: 0 !important;
        border-radius: 8px !important;
      }
      /* Force the iframe to fill its edit-area (overrides any JS-set px height) */
      :host ::ng-deep .tox-edit-area__iframe {
        height: 100% !important;
      }
      :host ::ng-deep .tox-statusbar { display: none !important; }
      /* Toolbar wraps its buttons onto new lines instead of scrolling
         horizontally when the editor is too narrow to fit them on one row. */
      :host ::ng-deep .tox-tinymce,
      :host ::ng-deep .tox .tox-editor-header {
        max-width: 100% !important;
      }
      :host ::ng-deep .tox .tox-toolbar,
      :host ::ng-deep .tox .tox-toolbar__primary,
      :host ::ng-deep .tox .tox-toolbar__overflow {
        flex-wrap: wrap !important;
        overflow-x: hidden !important;
      }
    `,
  ],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: '/tinymce/tinymce.min.js' },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TinyMCEEditorComponent),
      multi: true,
    },
  ],
})
export class TinyMCEEditorComponent implements ControlValueAccessor, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly isBrowser = isPlatformBrowser(this.platformId);

  /**
   * 'full' shows the complete toolbar; 'simple' hides it (text-only editing);
   * 'article' is long-form authoring — article typography, image resizing and
   * the floating quick toolbars.
   */
  readonly mode = input<'full' | 'simple' | 'article'>('full');

  /** Fires once TinyMCE has actually finished initializing — the underlying
   *  library/iframe load takes a beat after the host form control gets its
   *  value, so callers wait on this (not a guessed delay) to know when it is
   *  safe to stop showing a loading placeholder. */
  @Output() readonly editorReady = new EventEmitter<void>();

  @ViewChild(EditorComponent) private editorComponent?: EditorComponent;

  protected readonly innerControl = new FormControl('');

  protected get activeEditorConfig() {
    switch (this.mode()) {
      case 'simple':
        return SIMPLE_CONFIG;
      case 'article':
        return ARTICLE_CONFIG;
      default:
        return FULL_CONFIG;
    }
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly sub = new Subscription();

  constructor() {
    this.sub.add(
      this.innerControl.valueChanges.subscribe((value) => {
        this.onChange(value ?? '');
        this.onTouched();
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** One code path for every kind of token: the catalogue's `insertText` decides
   *  what lands in the document, so nothing is assembled by hand here. */
  insertToken(insertText: string, label: string): void {
    const editor = this.editorComponent?.editor;
    if (!editor) return;
    editor.insertContent(tokenBadgeHtml(tokenKeyFromInsertText(insertText), label));
  }

  /** Inserts a Start marker together with its End, so no region is left open. */
  insertTokenRegion(start: EditorToken, end: EditorToken, middle = ''): void {
    const editor = this.editorComponent?.editor;
    if (!editor) return;
    editor.insertContent(tokenRegionHtml(start, end, middle));
  }

  /**
   * Every distinct token key currently in the body that `/token-values` can answer
   * for. Chips reading a repeating region are left out: their text comes from the
   * letter's `lists` block, and that endpoint serves single values only.
   */
  tokenKeys(): string[] {
    const editor = this.editorComponent?.editor;
    if (!editor) return [];
    const keys = Array.from(editor.getBody().querySelectorAll<HTMLElement>('[data-token]'))
      .filter((badge) => !badge.hasAttribute('data-token-list'))
      .map((badge) => badge.getAttribute('data-token') ?? '')
      .filter(Boolean);
    return [...new Set(keys)];
  }

  /**
   * Repoints every chip at the values just fetched, leaving the adviser's own
   * text untouched. One `transact` so the whole refresh is a single Ctrl+Z.
   *
   * Region chips are skipped: `/token-values` has no list values to give, so
   * refreshing them would replace a real "Nationwide, Halifax" with its label.
   * Those follow the letter — reopen it to re-read them.
   */
  applyTokenValues(values: Record<string, string | null>): void {
    const editor = this.editorComponent?.editor;
    if (!editor) return;
    const badges = Array.from(
      editor.getBody().querySelectorAll<HTMLElement>('[data-token]:not([data-token-list])'),
    );
    if (!badges.length) return;

    editor.undoManager.transact(() => {
      for (const badge of badges) {
        const key = badge.getAttribute('data-token') ?? '';
        const label = badge.getAttribute('data-token-label') || key;
        setTokenBadgeContent(badge, tokenValueDisplay(key, label, values));
      }
    });
    editor.fire('change');
  }

  // ── ControlValueAccessor ──────────────────────────────────────

  writeValue(value: string): void {
    this.innerControl.setValue(value ?? '', { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.innerControl.disable({ emitEvent: false });
    } else {
      this.innerControl.enable({ emitEvent: false });
    }
  }
}
